import {
  CodeIcon,
  FileIcon,
  type IconBoxColors,
  ImageIcon,
  PlayIcon,
  TableIcon,
  VolumeIcon,
} from "@hightouch/htui";
import { getFileExtension } from "@hightouch/lib/util/mime-types";
import { isImage, isVideo, resolveAttachmentMimeType } from "./file-processing";

/**
 * The visual bucket an attachment is drawn in. Purely presentational: it picks
 * an icon and a tint, and says nothing about whether a file may be uploaded,
 * previewed, or navigated to.
 */
export type AttachmentCategory =
  | "document"
  | "spreadsheet"
  | "image"
  | "video"
  | "audio"
  | "code"
  | "unknown";

/**
 * Specific types only. `text/plain` is deliberately absent so a `.csv` the
 * browser reported as plain text still reaches the extension table below and
 * lands on the spreadsheet treatment rather than the generic text one.
 *
 * A `Map` rather than an object literal: these keys come from the browser and
 * from the database, and an object lookup would return an inherited
 * `Object.prototype` member for a type named `toString`.
 */
const CATEGORY_BY_MIME_TYPE = new Map<string, AttachmentCategory>([
  ["application/pdf", "document"],
  ["application/msword", "document"],
  [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "document",
  ],
  ["application/vnd.oasis.opendocument.text", "document"],
  [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "document",
  ],
  ["application/vnd.ms-powerpoint", "document"],
  ["application/rtf", "document"],
  ["text/rtf", "document"],
  ["text/markdown", "document"],
  ["text/csv", "spreadsheet"],
  ["text/tab-separated-values", "spreadsheet"],
  ["application/vnd.ms-excel", "spreadsheet"],
  [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "spreadsheet",
  ],
  ["application/vnd.oasis.opendocument.spreadsheet", "spreadsheet"],
  ["text/html", "code"],
  ["application/xhtml+xml", "code"],
  ["application/json", "code"],
  ["text/json", "code"],
  ["text/xml", "code"],
  ["application/xml", "code"],
  ["text/css", "code"],
  ["text/javascript", "code"],
  ["application/javascript", "code"],
  ["text/typescript", "code"],
  ["application/sql", "code"],
  ["text/x-sql", "code"],
  ["application/yaml", "code"],
  ["text/yaml", "code"],
]);

/**
 * Consulted only when the MIME type says nothing specific, which is the common
 * case for source files: browsers report nothing for `.sql`, `.py` or `.yml`,
 * so `resolveAttachmentMimeType` lands them on the generic binary type.
 *
 * This affects the icon and tint alone. It does not widen what may be uploaded,
 * and it never becomes the recorded MIME type.
 */
const CATEGORY_BY_EXTENSION = new Map<string, AttachmentCategory>([
  ["mp3", "audio"],
  ["wav", "audio"],
  ["m4a", "audio"],
  ["aac", "audio"],
  ["ogg", "audio"],
  ["oga", "audio"],
  ["opus", "audio"],
  ["flac", "audio"],
  ["csv", "spreadsheet"],
  ["tsv", "spreadsheet"],
  ["xls", "spreadsheet"],
  ["xlsx", "spreadsheet"],
  ["xlsm", "spreadsheet"],
  ["ods", "spreadsheet"],
  ["numbers", "spreadsheet"],
  ["html", "code"],
  ["htm", "code"],
  ["xhtml", "code"],
  ["json", "code"],
  ["xml", "code"],
  ["css", "code"],
  ["scss", "code"],
  ["less", "code"],
  ["js", "code"],
  ["jsx", "code"],
  ["mjs", "code"],
  ["cjs", "code"],
  ["ts", "code"],
  ["tsx", "code"],
  ["sql", "code"],
  ["yaml", "code"],
  ["yml", "code"],
  ["toml", "code"],
  ["graphql", "code"],
  ["sh", "code"],
  ["py", "code"],
  ["rb", "code"],
  ["go", "code"],
  ["rs", "code"],
  ["java", "code"],
  ["kt", "code"],
  ["php", "code"],
  ["c", "code"],
  ["h", "code"],
  ["cpp", "code"],
  ["pdf", "document"],
  ["doc", "document"],
  ["docx", "document"],
  ["odt", "document"],
  ["rtf", "document"],
  ["txt", "document"],
  ["log", "document"],
  ["md", "document"],
  ["markdown", "document"],
  ["ppt", "document"],
  ["pptx", "document"],
  ["key", "document"],
  ["pages", "document"],
  ["epub", "document"],
]);

/**
 * Picks the visual bucket for an attachment.
 *
 * MIME wins where it is specific, because a file can be renamed but its
 * recorded type is what every other part of the attachment pipeline believes.
 * The extension only decides when the type is uninformative or generic text.
 */
export const attachmentCategory = (
  mimeType: string,
  fileName?: string,
): AttachmentCategory => {
  const resolved = resolveAttachmentMimeType(mimeType, fileName);

  if (isVideo(resolved, fileName)) return "video";
  if (isImage(resolved)) return "image";
  if (resolved.startsWith("audio/")) return "audio";

  const fromMimeType = CATEGORY_BY_MIME_TYPE.get(resolved.toLowerCase());
  if (fromMimeType) return fromMimeType;

  const extension = fileName ? getFileExtension(fileName).slice(1) : "";
  const fromExtension = CATEGORY_BY_EXTENSION.get(extension);
  if (fromExtension) return fromExtension;

  return resolved.startsWith("text/") ? "document" : "unknown";
};

export type AttachmentPresentation = {
  /** Every htui glyph shares one generated signature, so one of them types the slot. */
  icon: typeof FileIcon;
  /** `IconBox` shade; it owns the tile fill, border, and glyph color. */
  color: IconBoxColors;
};

/**
 * Type indicators, not status indicators: the filename beside them carries the
 * same information, so the chip stays readable without the color.
 */
const PRESENTATION_BY_CATEGORY: Record<
  AttachmentCategory,
  AttachmentPresentation
> = {
  document: { icon: FileIcon, color: "blue" },
  spreadsheet: { icon: TableIcon, color: "green" },
  image: { icon: ImageIcon, color: "indigo" },
  video: { icon: PlayIcon, color: "pink" },
  audio: { icon: VolumeIcon, color: "teal" },
  code: { icon: CodeIcon, color: "orange" },
  unknown: { icon: FileIcon, color: "gray" },
};

export const attachmentPresentation = (
  category: AttachmentCategory,
): AttachmentPresentation => PRESENTATION_BY_CATEGORY[category];

export const ATTACHMENT_CATEGORY_GROUPS: {
  category: AttachmentCategory;
  label: string;
}[] = [
  { category: "document", label: "Documents" },
  { category: "image", label: "Images" },
  { category: "spreadsheet", label: "Spreadsheets" },
  { category: "video", label: "Videos" },
  { category: "audio", label: "Audio" },
  { category: "code", label: "Code" },
  { category: "unknown", label: "Other files" },
];
